import { peekBranding } from "@/lib/branding";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return iso; }
};
const fmtDay = (isoDay) => {
  if (!isoDay) return "";
  try {
    return new Date(`${isoDay}T12:00:00`).toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric" });
  } catch { return isoDay; }
};

/**
 * Employer punch request — shown when an admin/owner taps a "forgot to
 * clock in" notification. Yes = accept the employee's stated arrival time
 * (timer keeps running untouched). No = reveal a time picker that corrects
 * the punch-in time (same business day only).
 */
const PunchRequestDialog = ({ notification, onClose, testid = "punch-request" }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [overrideTime, setOverrideTime] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/time-entries/${notification.meta.entry_id}`);
        setEntry(data);
      } catch {
        toast.error("Could not load the punch request");
        onClose();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolve = async (approve) => {
    setBusy(true);
    try {
      const body = { approve };
      if (!approve) {
        if (!overrideTime) { toast.error("Pick the corrected time"); setBusy(false); return; }
        body.time = overrideTime;
      }
      const { data } = await api.post(`/time-entries/${entry.entry_id}/oops-resolve`, body);
      setEntry(data);
      setCorrecting(false);
      toast.success(approve ? "Confirmed — their timer keeps running" : `Clock-in corrected to ${fmtTime(data.timestamp)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve the request");
    } finally { setBusy(false); }
  };

  const name = entry?.employee_name || notification.meta?.employee_name || "The employee";
  const pending = entry?.oops_status === "pending";
  const resolvedLabel = entry?.oops_status === "confirmed" ? "Confirmed" : entry?.oops_status === "corrected" ? "Time corrected" : null;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3" data-testid={`${testid}-dialog`}>
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3.5">
          <img src="/api/branding/icon/elite-logo-dark.png" alt="" className="logo-dark-only h-8 w-auto object-contain" />
          <img src="/api/branding/icon/elite-logo-light.png" alt="" className="logo-light-only h-8 w-auto object-contain" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8fa4f0]">{peekBranding()?.app_name || "Elite Mechanical"}</p>
            <p className="truncate text-sm font-semibold text-white" data-testid={`${testid}-title`}>Employer punch request</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <>
              <p className="text-sm leading-relaxed text-white/80" data-testid={`${testid}-question`}>
                <span className="font-semibold text-white">{name}</span> says they arrived at{" "}
                <span className="font-mono font-semibold text-white">{fmtTime(entry?.timestamp)}</span> on {fmtDay(entry?.day)}.
                Their timer is already running from that time.
              </p>
              {pending ? (
                <>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/45">
                    Are they telling the truth?
                  </p>
                  {!correcting ? (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => resolve(true)} disabled={busy}
                        data-testid={`${testid}-yes`}
                        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Yes
                      </button>
                      <button
                        onClick={() => {
                          // Default the picker to the punch's time (Toronto —
                          // the backend interprets correction times as Toronto).
                          setOverrideTime(
                            new Date(entry?.timestamp || Date.now()).toLocaleTimeString("en-GB", {
                              timeZone: "America/Toronto", hour12: false, hour: "2-digit", minute: "2-digit",
                            }),
                          );
                          setCorrecting(true);
                        }}
                        disabled={busy}
                        data-testid={`${testid}-no`}
                        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60"
                      >
                        <X className="h-4 w-4" /> No
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50">
                        Correct arrival time (today only)
                      </label>
                      <input
                        type="time"
                        value={overrideTime}
                        onChange={(e) => setOverrideTime(e.target.value)}
                        data-testid={`${testid}-override-time`}
                        className="h-11 w-full rounded border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setCorrecting(false)} disabled={busy}
                          data-testid={`${testid}-override-cancel`}
                          className="rounded-md border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white/70"
                        >
                          Back
                        </button>
                        <button
                          onClick={() => resolve(false)} disabled={busy}
                          data-testid={`${testid}-override-save`}
                          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
                        >
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Set time
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200" data-testid={`${testid}-resolved`}>
                  {resolvedLabel || "Already handled"}
                  {entry?.resolved_at ? "" : ""}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PunchRequestDialog;
